import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export default function AuthActivate() {
  const [params] = useSearchParams();

  const [status, setStatus] = useState("loading"); // loading | ready | done | error
  const [message, setMessage] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [codeSecret, setCodeSecret] = useState("");
  const [requiresCode, setRequiresCode] = useState(true); // par défaut: on le demande (sécurité)
  const [submitting, setSubmitting] = useState(false);
  const [canRenew, setCanRenew] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [renewed, setRenewed] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const [retryUnknown, setRetryUnknown] = useState(false);
  const [now, setNow] = useState(Date.now());

  const token = params.get("token") || "";

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setStatus("loading");
      setCanRenew(false);
      setRenewed(false);
      setRetryAt(0);
      setRetryUnknown(false);
      if (!token) {
        setStatus("error");
        setMessage("Token manquant.");
        return;
      }

      try {
        const res = await fetch(
          `/api/v2/auth/activate?token=${encodeURIComponent(token)}`
        );
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok || !json?.ok) {
          setStatus("error");
          setMessage(json?.error || "Lien invalide.");
          setCanRenew(json?.code === "ACTIVATION_EXPIRED" && json?.can_renew === true);
          return;
        }

        setUsername(json?.username || "");
        setEmail(json?.email || "");
        setRequiresCode(
          typeof json?.requires_code === "boolean" ? json.requires_code : true
        );

        setStatus("ready");
        setCanRenew(json?.can_renew === true);
        setMessage(json?.message || "Merci de définir votre mot de passe.");
      } catch {
        if (cancelled) return;
        setStatus("error");
        setMessage("Erreur réseau.");
      }
    }

    run();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!retryAt) return;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= retryAt) setRetryAt(0);
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAt]);

  async function onRenew() {
    if (renewing || renewed || submitting || !canRenew || retryUnknown || retryAt > Date.now()) return;
    setRenewing(true);
    try {
      const res = await fetch("/api/v2/auth/renew-activation", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token })
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 429) {
        const raw = res.headers.get("Retry-After");
        const seconds = raw && /^\d+$/.test(raw) ? Number(raw) : NaN;
        const deadline = Number.isFinite(seconds) ? Date.now() + seconds * 1000 : Date.parse(raw);
        setNow(Date.now());
        setRetryAt(Number.isFinite(deadline) ? deadline : 0);
        setRetryUnknown(!Number.isFinite(deadline));
        setMessage("Trop de demandes. Patientez, puis rechargez la page pour réessayer.");
      } else if (!res.ok || !json?.ok) {
        setMessage(json?.error || "L'envoi n'a pas abouti. Réessayez plus tard.");
        if (res.status === 409) setCanRenew(false);
      } else {
        setRenewed(true);
        setStatus("error");
        setMessage(json.message);
      }
    } catch {
      setMessage("Erreur réseau. Réessayez ; si le renvoi a déjà abouti, consultez le dernier email reçu.");
    } finally {
      setRenewing(false);
    }
  }

  function validatePasswordLocal(a, b) {
    if (a.length < 6) return "Mot de passe trop court (min 6 caractères).";
    if (!/^[A-Za-z0-9]+$/.test(a))
      return "Mot de passe invalide (alphanumérique uniquement).";
    if (a !== b) return "Les deux mots de passe ne correspondent pas.";
    return null;
  }

  const codeSecretClean = useMemo(
    () => String(codeSecret || "").replace(/\s+/g, ""),
    [codeSecret]
  );

  function validateCodeLocal(code) {
    const c = String(code || "").trim();
    if (!c) return "Code secret requis.";
    if (!/^\d{6}$/.test(c))
      return "Code secret invalide (6 chiffres).";
    return null;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMessage("");

    const err = validatePasswordLocal(pw1, pw2);
    if (err) {
      setStatus("ready");
      setMessage(err);
      return;
    }

    if (requiresCode) {
      const errCode = validateCodeLocal(codeSecretClean);
      if (errCode) {
        setStatus("ready");
        setMessage(errCode);
        return;
      }
    }

    setSubmitting(true);
    
    const payload = {
      token,
      password: pw1,
      codeSecret: requiresCode ? codeSecretClean : "",
    };
    
    try {
      const res = await fetch("/api/v2/auth/set-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok) {
        setSubmitting(false);
        const expired = json?.code === "ACTIVATION_EXPIRED";
        setStatus(expired ? "error" : "ready");
        setCanRenew(expired && json?.can_renew === true);
        setMessage(json?.error || "Impossible de finaliser l’inscription.");
        return;
      }

      setSubmitting(false);
      setStatus("done");
      setMessage(json?.message || "Inscription finalisée.");
    } catch {
      setSubmitting(false);
      setStatus("ready");
      setMessage("Erreur réseau.");
    }
  }

  const isErrMessage = (m) => {
    const s = String(m || "");
    return (
      s.includes("correspond") ||
      s.includes("alphanum") ||
      s.includes("court") ||
      s.includes("Impossible") ||
      s.includes("Code secret") ||
      s.includes("Token") ||
      s.includes("tentatives") ||
      s.includes("verrouillé") ||
      s.includes("verrouille") ||
      s.includes("expir")
    );
  };

  const S = {
    container: {
      minHeight: "100dvh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f3f4f6",
      padding: "clamp(24px, 4dvh, 48px) 28px",
      fontFamily:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    card: {
      width: "clamp(720px, 52vw, 1100px)",
      maxWidth: "calc(100vw - 56px)",
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "24px",
      padding: "clamp(38px, 4vw, 54px)",
      boxShadow: "0 22px 56px rgba(15,23,42,0.12)",
    },
    h1: { margin: 0, fontSize: "40px", fontWeight: 950, color: "#111827" },
    p: {
      marginTop: "10px",
      color: "#4b5563",
      lineHeight: 1.55,
      fontSize: "16px",
    },
    label: { fontSize: "15px", fontWeight: 800, color: "#111827" },
    input: {
      width: "100%",
      padding: "15px 14px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      outline: "none",
      fontSize: "16px",
    },
    form: { marginTop: "22px", display: "grid", gap: "16px" },
    row: { display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "14px" },
    btn: {
      padding: "15px 22px",
      borderRadius: "9999px",
      border: "1px solid #111827",
      background: "#111827",
      color: "#fff",
      fontSize: "15px",
      fontWeight: 900,
      cursor: "pointer",
    },
    alertOk: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #bbf7d0",
      background: "#f0fdf4",
      color: "#166534",
      fontSize: "14px",
      lineHeight: 1.5,
    },
    alertErr: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      fontSize: "14px",
      lineHeight: 1.5,
    },
    hint: { marginTop: "8px", fontSize: "13px", color: "#6b7280" },
    codeHint: { marginTop: "8px", fontSize: "13px", color: "#6b7280" },
  };

  return (
    <div style={S.container}>
      <div style={S.card}>
        <h1 style={S.h1}>Finaliser mon inscription</h1>

        {status === "loading" && <div style={S.p}>Validation du lien…</div>}

        {status === "error" && (
          <div style={renewed ? S.alertOk : S.alertErr} role="status">
            <b>{message || "Lien invalide."}</b>
            {canRenew && !renewed && (
              <div style={S.row}>
                <button type="button" style={S.btn} onClick={onRenew}
                  disabled={renewing || retryUnknown || retryAt > now}>
                  {renewing ? "Envoi…" : "Recevoir un nouveau lien"}
                </button>
                {retryAt > now && <span>Réessayez dans {Math.ceil((retryAt - now) / 1000)} s.</span>}
              </div>
            )}
            {!renewed && <p style={S.hint}>En cas de difficulté, contactez christophe.baillard@aphp.fr.</p>}
          </div>
        )}

        {status === "ready" && (
          <>
            <p style={S.p}>
              {message || "Merci de définir votre mot de passe."}
              {email ? (
                <>
                  <br />
                  Adresse mail : <b>{email}</b>
                </>
              ) : null}
            </p>

            <form style={S.form} onSubmit={onSubmit}>
              {requiresCode ? (
                <div>
                  <div style={S.label}>Code secret (6 chiffres)</div>
                  <input
                    style={S.input}
                    value={codeSecret}
                    onChange={(e) =>
                      setCodeSecret(
                        String(e.target.value || "").replace(/[^\d]/g, "").slice(0, 6)
                      )
                    }
                    inputMode="numeric"
                    type="text"
                    autoComplete="one-time-code"
                    placeholder="Ex: 123456"
                  />
                  <div style={S.codeHint}>
                    Ce code est présent dans l’email d’inscription (envoyé par le service).
                  </div>
                </div>
              ) : null}

              <div>
                <div style={S.label}>Mot de passe</div>
                <div style={{ position: "relative" }}>
                  <input
                    style={{ ...S.input, paddingRight: "45px" }}
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    type={showPassword1 ? "text" : "password"}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword1(!showPassword1)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      fontSize: "18px",
                      color: "#6b7280",
                    }}
                    title={showPassword1 ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword1 ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>
                <div style={S.hint}>
                  Min 6 caractères, alphanumérique uniquement.
                </div>
              </div>

              <div>
                <div style={S.label}>Confirmer le mot de passe</div>
                <div style={{ position: "relative" }}>
                  <input
                    style={{ ...S.input, paddingRight: "45px" }}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    type={showPassword2 ? "text" : "password"}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword2(!showPassword2)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      fontSize: "18px",
                      color: "#6b7280",
                    }}
                    title={showPassword2 ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword2 ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>
              </div>

              <div style={S.row}>
                <button type="submit" style={S.btn} disabled={submitting}>
                  {submitting ? "Validation…" : "Valider"}
                </button>
              </div>
            </form>

            {canRenew && !renewed && <div style={S.row}>
              <button type="button" style={S.btn} onClick={onRenew}
                disabled={renewing || submitting || retryUnknown || retryAt > now}>
                {renewing ? "Envoi…" : "Recevoir un nouveau lien"}
              </button>
              {retryAt > now && <span>Réessayez dans {Math.ceil((retryAt - now) / 1000)} s.</span>}
            </div>}

            {message && isErrMessage(message) ? (
              <div style={S.alertErr}>
                <b>{message}</b>
              </div>
            ) : null}
          </>
        )}

        {status === "done" && (
          <div style={S.alertOk}>
            <b>{message || "Inscription finalisée."}</b>
            {email ? (
              <div>
                Adresse mail : <b>{email}</b>
              </div>
            ) : null}
            <div style={{ marginTop: "10px" }}>
              <button
                type="button"
                style={S.btn}
                onClick={() => { window.location.href = "/auth"; }}
              >
                Continuer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
